import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { History, Edit } from "lucide-react";
import type { DocumentVersion } from "@/types/document";

interface VersionSelectorProps {
  versions: DocumentVersion[];
  currentVersion: number;
  onVersionSelect: (version: DocumentVersion) => void;
  isLoading?: boolean;
}

export function VersionSelector({ 
  versions, 
  currentVersion, 
  onVersionSelect, 
  isLoading = false 
}: VersionSelectorProps) {
  const [open, setOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<string>(currentVersion.toString());

  const handleVersionSelect = () => {
    const version = versions.find(v => v.version_number.toString() === selectedVersion);
    if (version) {
      onVersionSelect(version);
      setOpen(false);
    }
  };

  const getVersionLabel = (version: DocumentVersion) => {
    const isCurrent = version.version_number === currentVersion;
    const isLatest = version.version_number === Math.max(...versions.map(v => v.version_number));
    
    if (isCurrent && isLatest) {
      return "Current (Latest)";
    } else if (isCurrent) {
      return "Current";
    } else if (isLatest) {
      return "Latest";
    } else {
      return `Version ${version.version_number}`;
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" disabled={isLoading}>
          <History className="h-4 w-4 mr-2" />
          Version {currentVersion}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Select Document Version</DialogTitle>
          <DialogDescription>
            Choose which version of the document you want to edit. The current version is what you're currently viewing.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Available Versions</label>
            <Select value={selectedVersion} onValueChange={setSelectedVersion}>
              <SelectTrigger>
                <SelectValue placeholder="Select a version" />
              </SelectTrigger>
              <SelectContent>
                {versions.map((version) => (
                  <SelectItem 
                    key={version.id} 
                    value={version.version_number.toString()}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span>{getVersionLabel(version)}</span>
                      <Badge variant="outline" className="ml-2">
                        {format(new Date(version.created_at), 'MMM d, yyyy')}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Version Details</label>
            <div className="text-sm text-muted-foreground space-y-1">
              {(() => {
                const version = versions.find(v => v.version_number.toString() === selectedVersion);
                if (!version) return null;
                
                return (
                  <>
                    <p><strong>Version:</strong> {version.version_number}</p>
                    <p><strong>Created:</strong> {format(new Date(version.created_at), 'MMM d, yyyy h:mm a')}</p>
                    <p><strong>Status:</strong> {getVersionLabel(version)}</p>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleVersionSelect}>
            <Edit className="h-4 w-4 mr-2" />
            Edit This Version
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 